import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Save, X, Search, Filter, GripVertical, 
  Eye, EyeOff, Star, MoreVertical, RefreshCw, Image as ImageIcon, Images
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ImageUploader } from '../../components/ui/ImageUploader';
import { MultiMediaUploader } from '../../components/ui/MultiMediaUploader';

const categories = ['Partitions', 'Doors', 'Windows', 'Glass', 'Railings', 'Bathroom', 'Office', 'Elevation', 'Sliding', 'Louvers'];
const icons = ['grid-3x3', 'door-open', 'app-window', 'square', 'droplets', 'building', 'layers', 'fence', 'move-horizontal', 'blinds'];

const defaultService = {
  id: '', name: '', icon: 'grid-3x3', description: '', short_description: '',
  benefits: [''], image: '', images: [], videos: [], category: 'Partitions',
  is_featured: false, show_on_homepage: true, order: 0, is_active: true,
  seo_title: '', seo_description: ''
};

export const ServicesManager = () => {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const res = await apiService.getServices(token);
      setServices(res.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 50);

  const handleSave = async () => {
    const { item } = editModal;
    if (!item.id || !item.name) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const cleanItem = { ...item, benefits: item.benefits.filter(b => b.trim()) };
      if (item.isNew) {
        await apiService.createService(token, cleanItem);
        toast.success('Service created successfully!');
      } else {
        await apiService.updateService(token, item.id, cleanItem);
        toast.success('Service updated successfully!');
      }
      setEditModal({ open: false, item: null });
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving service');
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteService(token, deleteDialog.item.id);
      toast.success('Service deleted');
      setDeleteDialog({ open: false, item: null });
      fetchServices();
    } catch (error) {
      toast.error('Error deleting service');
    }
  };

  const openEditModal = (item = null) => {
    setEditModal({
      open: true,
      item: item ? { ...item, images: item.images || [], videos: item.videos || [], isNew: false } : { ...defaultService, id: `service-${Date.now()}`, order: services.length + 1, images: [], videos: [], isNew: true }
    });
  };

  const updateBenefit = (index, value) => {
    const benefits = [...editModal.item.benefits];
    benefits[index] = value;
    setEditModal({ ...editModal, item: { ...editModal.item, benefits } });
  };

  const addBenefit = () => {
    setEditModal({ ...editModal, item: { ...editModal.item, benefits: [...editModal.item.benefits, ''] } });
  };

  const removeBenefit = (index) => {
    const benefits = editModal.item.benefits.filter((_, i) => i !== index);
    setEditModal({ ...editModal, item: { ...editModal.item, benefits } });
  };

  const filtered = services.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase());
    const matchCategory = filterCategory === 'all' || s.category === filterCategory;
    return matchSearch && matchCategory;
  });

  return (
    <div className="space-y-6" data-testid="services-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Services Manager</h1>
          <p className="text-slate-500">Manage your service offerings</p>
        </div>
        <Button onClick={() => openEditModal()} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> Add Service
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search services..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchServices}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Services Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-sky-500" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-slate-500">No services found</p>
            <Button onClick={() => openEditModal()} variant="outline" className="mt-4">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Service
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((service) => {
            const totalImages = 1 + (service.images?.length || 0); // Main image + additional
            const totalVideos = service.videos?.length || 0;
            return (
            <Card key={service.id} className={`overflow-hidden ${!service.is_active ? 'opacity-60' : ''}`}>
              <div className="aspect-video relative bg-slate-100">
                <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
                <div className="absolute top-2 left-2 flex gap-1">
                  {service.is_featured && (
                    <span className="px-2 py-1 bg-orange-500 text-white text-xs rounded">Featured</span>
                  )}
                  {!service.is_active && (
                    <span className="px-2 py-1 bg-slate-500 text-white text-xs rounded">Inactive</span>
                  )}
                </div>
                {/* Image/Video count badge */}
                <div className="absolute bottom-2 left-2 flex gap-1">
                  {totalImages > 1 && (
                    <span className="px-2 py-1 bg-black/60 text-white text-xs rounded flex items-center gap-1">
                      <Images className="w-3 h-3" /> {totalImages}
                    </span>
                  )}
                  {totalVideos > 0 && (
                    <span className="px-2 py-1 bg-pink-500/80 text-white text-xs rounded">
                      {totalVideos} video{totalVideos > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="absolute top-2 right-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="secondary" className="w-8 h-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditModal(service)}>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item: service })} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-sky-600 font-medium">{service.category}</span>
                  <span className="text-xs text-slate-400">#{service.order}</span>
                </div>
                <h3 className="font-semibold text-slate-900 line-clamp-1">{service.name}</h3>
                <p className="text-sm text-slate-500 line-clamp-2 mt-1">{service.short_description || service.description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {service.benefits?.slice(0, 2).map((b, i) => (
                    <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">{b}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )})}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editModal.item?.isNew ? 'Add New Service' : 'Edit Service'}</DialogTitle>
            <DialogDescription>Fill in the service details below</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-6 py-4">
              {/* Main Image with Free Crop */}
              <ImageUploader
                label="Main Service Image"
                value={editModal.item.image}
                onChange={(url) => setEditModal({ ...editModal, item: { ...editModal.item, image: url } })}
                allowFreeCrop={true}
                token={token}
              />

              {/* Multiple Images & Videos Gallery */}
              <MultiMediaUploader
                label="Service Gallery (Multiple Images & Videos)"
                images={editModal.item.images || []}
                videos={editModal.item.videos || []}
                onImagesChange={(images) => setEditModal({ ...editModal, item: { ...editModal.item, images } })}
                onVideosChange={(videos) => setEditModal({ ...editModal, item: { ...editModal.item, videos } })}
                maxImages={10}
                maxVideos={3}
                showVideos={true}
                token={token}
              />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Service ID *</Label>
                  <Input
                    value={editModal.item.id}
                    onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, id: e.target.value } })}
                    disabled={!editModal.item.isNew}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={editModal.item.category}
                    onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, category: value } })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input
                  value={editModal.item.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const updates = { name };
                    if (editModal.item.isNew) updates.id = generateSlug(name);
                    setEditModal({ ...editModal, item: { ...editModal.item, ...updates } });
                  }}
                  placeholder="e.g., Aluminium Partitions"
                />
              </div>

              <div className="space-y-2">
                <Label>Short Description</Label>
                <Input
                  value={editModal.item.short_description}
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, short_description: e.target.value } })}
                  placeholder="Brief one-line description"
                  maxLength={100}
                />
              </div>

              <div className="space-y-2">
                <Label>Full Description *</Label>
                <Textarea
                  value={editModal.item.description}
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, description: e.target.value } })}
                  placeholder="Detailed service description..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Benefits</Label>
                {editModal.item.benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={benefit}
                      onChange={(e) => updateBenefit(index, e.target.value)}
                      placeholder={`Benefit ${index + 1}`}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeBenefit(index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addBenefit}>
                  <Plus className="w-4 h-4 mr-1" /> Add Benefit
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <Select
                    value={editModal.item.icon}
                    onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, icon: value } })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {icons.map(icon => <SelectItem key={icon} value={icon}>{icon}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={editModal.item.order}
                    onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, order: parseInt(e.target.value) || 0 } })}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editModal.item.is_active}
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_active: checked } })}
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editModal.item.is_featured}
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_featured: checked } })}
                  />
                  <Label>Featured</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editModal.item.show_on_homepage}
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, show_on_homepage: checked } })}
                  />
                  <Label>Show on Homepage</Label>
                </div>
              </div>

              {/* SEO */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium text-slate-900">SEO Settings</h4>
                <div className="space-y-2">
                  <Label>SEO Title</Label>
                  <Input
                    value={editModal.item.seo_title}
                    onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, seo_title: e.target.value } })}
                    placeholder="SEO page title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SEO Description</Label>
                  <Textarea
                    value={editModal.item.seo_description}
                    onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, seo_description: e.target.value } })}
                    placeholder="SEO meta description"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-sky-500 hover:bg-sky-600">
              <Save className="w-4 h-4 mr-2" /> Save Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Service?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDialog.item?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ServicesManager;
